export type DashboardColor = "none" | "red" | "green" | "blue" | "yellow";

export interface ArticleDisplayFilter {
  negativeOnly?: boolean;
  revisionOnly?: boolean;
}

interface MonitoringDetailUrlParams {
  monitoringId?: string | null;
  tab?: "settings" | "articles" | "runs";
  date?: string | null;
  negativeOnly?: boolean;
  revisionOnly?: boolean;
  articleRangeDays?: number | string | null;
}

interface DashboardSettingLike {
  visibleItemIds?: unknown;
  itemOrder?: unknown;
  cardColors?: Record<string, DashboardColor>;
  articleLimits?: Record<string, number>;
  articleDisplayFilters?: Record<string, ArticleDisplayFilter>;
  createdAt?: string;
  updatedAt?: string;
}

interface NormalizeDashboardSettingParams {
  setting?: DashboardSettingLike;
  monitoringIds: string[];
  pressReleaseMonitoringId?: string | null;
}

interface NormalizeIdOptions {
  knownMonitoringIds: Set<string>;
  pressReleaseMonitoringId?: string | null;
}

export interface NormalizedDashboardSetting {
  visibleItemIds: string[];
  itemOrder: string[];
  cardColors: Record<string, DashboardColor>;
  articleLimits: Record<string, number>;
  articleDisplayFilters: Record<string, ArticleDisplayFilter>;
  hasPressReleaseMonitoring: boolean;
}

export const pressReleaseItemId = "press-release-monitoring";

const hasSavedDashboardSetting = (setting?: DashboardSettingLike) =>
  Boolean(setting?.createdAt || setting?.updatedAt);

const normalizeDashboardId = (value: string, options: NormalizeIdOptions): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (options.pressReleaseMonitoringId && trimmed === options.pressReleaseMonitoringId) {
    return pressReleaseItemId;
  }
  if (trimmed === pressReleaseItemId) {
    return options.pressReleaseMonitoringId ? pressReleaseItemId : null;
  }
  return options.knownMonitoringIds.has(trimmed) ? trimmed : null;
};

const dedupePreservingOrder = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const normalizeDashboardIdList = (value: unknown, options: NormalizeIdOptions) => {
  if (!Array.isArray(value)) return [];
  return dedupePreservingOrder(
    value
      .filter((item): item is string => typeof item === "string")
      .map((item) => normalizeDashboardId(item, options))
      .filter((item): item is string => Boolean(item))
  );
};

const normalizeDashboardMap = <T>(
  value: Record<string, T> | undefined,
  options: NormalizeIdOptions
) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {} as Record<string, T>;
  return Object.entries(value).reduce<Record<string, T>>((acc, [rawKey, rawValue]) => {
    const normalizedKey = normalizeDashboardId(rawKey, options);
    if (normalizedKey) acc[normalizedKey] = rawValue;
    return acc;
  }, {});
};

const normalizeArticleDisplayFilterMap = (
  value: Record<string, ArticleDisplayFilter> | undefined,
  options: NormalizeIdOptions
) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce<Record<string, ArticleDisplayFilter>>((acc, [rawKey, rawValue]) => {
    const normalizedKey = normalizeDashboardId(rawKey, options);
    if (!normalizedKey) return acc;
    const normalizedFilter: ArticleDisplayFilter = {};
    if (rawValue?.negativeOnly === true) normalizedFilter.negativeOnly = true;
    if (rawValue?.revisionOnly === true) normalizedFilter.revisionOnly = true;
    if (Object.keys(normalizedFilter).length > 0) acc[normalizedKey] = normalizedFilter;
    return acc;
  }, {});
};

export const buildDefaultDashboardItemIds = (
  monitoringIds: string[],
  hasPressReleaseMonitoring: boolean
) => (
  hasPressReleaseMonitoring ? [...monitoringIds, pressReleaseItemId] : [...monitoringIds]
);

export const normalizeDashboardSetting = ({
  setting,
  monitoringIds,
  pressReleaseMonitoringId,
}: NormalizeDashboardSettingParams): NormalizedDashboardSetting => {
  const hasPressReleaseMonitoring = Boolean(pressReleaseMonitoringId);
  const options: NormalizeIdOptions = {
    knownMonitoringIds: new Set(monitoringIds),
    pressReleaseMonitoringId,
  };
  const defaultItemIds = buildDefaultDashboardItemIds(monitoringIds, hasPressReleaseMonitoring);
  const normalizedVisibleItemIds = normalizeDashboardIdList(setting?.visibleItemIds, options);
  const normalizedItemOrder = normalizeDashboardIdList(setting?.itemOrder, options);
  const savedSetting = hasSavedDashboardSetting(setting);

  return {
    visibleItemIds: savedSetting ? normalizedVisibleItemIds : defaultItemIds,
    itemOrder: savedSetting ? normalizedItemOrder : defaultItemIds,
    cardColors: {
      ...normalizeDashboardMap(setting?.cardColors, options),
      ...(hasPressReleaseMonitoring ? { [pressReleaseItemId]: "none" as DashboardColor } : {}),
    },
    articleLimits: normalizeDashboardMap(setting?.articleLimits, options),
    articleDisplayFilters: normalizeArticleDisplayFilterMap(setting?.articleDisplayFilters, options),
    hasPressReleaseMonitoring,
  };
};

const normalizeArticleRangeDays = (value?: number | string | null) => {
  const numericValue = Number(value ?? 1);
  return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7 ? numericValue : 1;
};

export const buildDashboardArticleLookupParams = (articleRangeDays?: number | string | null) => {
  const normalizedArticleRangeDays = normalizeArticleRangeDays(articleRangeDays);
  return {
    articleRangeDays: normalizedArticleRangeDays,
    ...(normalizedArticleRangeDays === 1 ? { articleLookupMode: "CURRENT_RUN" as const } : {}),
  };
};

const normalizeArticleDate = (value?: string | null) => {
  const text = (value ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
};

export const buildPressReleaseDetailUrl = (
  pressReleaseMonitoringId?: string | null,
  filter: ArticleDisplayFilter = {},
  articleRangeDays?: number | string | null,
  date?: string | null
) => {
  return buildMonitoringDetailUrl({
    monitoringId: pressReleaseMonitoringId,
    tab: "articles",
    date,
    negativeOnly: filter.negativeOnly === true,
    revisionOnly: filter.revisionOnly === true,
    articleRangeDays,
  });
};

export const buildMonitoringDetailUrl = ({
  monitoringId,
  tab = "articles",
  date,
  negativeOnly = false,
  revisionOnly = false,
  articleRangeDays,
}: MonitoringDetailUrlParams) => {
  if (!monitoringId) return "/pressRelease";
  const params = new URLSearchParams({ monitoring: monitoringId, tab });
  const normalizedDate = normalizeArticleDate(date);
  if (normalizedDate) params.set("date", normalizedDate);
  if (negativeOnly) params.set("negativeOnly", "1");
  if (revisionOnly) params.set("revisionOnly", "1");
  params.set("articleRangeDays", String(normalizeArticleRangeDays(articleRangeDays)));
  return `/pressRelease/detail?${params.toString()}`;
};
